import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function AboutPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Matthew DOTSE',
      jobTitle: 'Web Entrepreneur',
      description: 'Web Entrepreneur basé à Lomé, au Togo. Créateur de plateformes numériques et de solutions digitales à l\'échelle internationale.',
      url: 'https://worldapeiron.com/about',
      image: 'https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/83d58e97feb31105ec513f51514567d5.png',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Lomé',
        addressCountry: 'TG',
      },
      knowsAbout: [
        'Création de sites web',
        'Création de plateformes digitales',
        'Développement de solutions numériques',
        'Entrepreneuriat web',
        'Intelligence artificielle',
        'Marketing digital',
        'SEO',
        'E-commerce',
        'Gestion de projets numériques',
        'Création d\'écosystèmes numériques',
        'Stratégie digitale',
      ],
      worksFor: {
        '@type': 'Organization',
        name: 'WorldApeiron',
        url: 'https://worldapeiron.com',
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const skills = [
    { name: 'Création de sites web', icon: 'ri-global-line', desc: 'Sites vitrines, e-commerce et applications web sur mesure.' },
    { name: 'Création de plateformes digitales', icon: 'ri-stack-line', desc: 'Écosystèmes modulaires avec authentification unifiée.' },
    { name: 'Développement de solutions numériques', icon: 'ri-code-box-line', desc: 'Applications métier, APIs et automatisation.' },
    { name: 'Entrepreneuriat web', icon: 'ri-lightbulb-flash-line', desc: 'De l\'idée au produit viable, en mode agile.' },
    { name: 'Intelligence artificielle', icon: 'ri-brain-line', desc: 'Intégration d\'IA pour le service client, l\'analyse et la création.' },
    { name: 'Marketing digital', icon: 'ri-megaphone-line', desc: 'Stratégie de croissance organique et acquisition.' },
    { name: 'SEO', icon: 'ri-search-line', desc: 'Référencement naturel, contenu et optimisation technique.' },
    { name: 'E-commerce', icon: 'ri-shopping-cart-line', desc: 'Marketplaces, boutiques en ligne et paiements intégrés.' },
    { name: 'Gestion de projets numériques', icon: 'ri-folder-chart-line', desc: 'Planification, exécution et livraison de projets tech.' },
    { name: 'Création d\'écosystèmes numériques', icon: 'ri-node-tree', desc: 'Modules interconnectés partageant une base commune.' },
    { name: 'Stratégie digitale', icon: 'ri-line-chart-line', desc: 'Alignement des objectifs business avec les leviers numériques.' },
  ];

  const HeroReveal = () => {
    const { ref, isVisible } = useScrollReveal(0.1);
    return (
      <div ref={ref} className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-4">
          Web Entrepreneur
        </span>
        <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-foreground-50 mb-4 leading-tight">
          Matthew <span className="text-gradient-primary">DOTSE</span>
        </h1>
        <p className="text-base md:text-lg text-foreground-400 max-w-xl mb-2">
          Créateur de solutions numériques et d'écosystèmes technologiques
        </p>
        <div className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
          <i className="ri-map-pin-line" />
          <span>Lomé, Togo</span>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
          >
            <i className="ri-article-line" />
            Lire mes articles
          </Link>
          <Link
            to="/author/matthew-dotse"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-300/60 text-foreground-50 font-semibold rounded-lg hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
          >
            <i className="ri-user-line" />
            Fiche auteur
          </Link>
        </div>
      </div>
    );
  };

  const VisionReveal = () => {
    const { ref, isVisible } = useScrollReveal(0.15);
    return (
      <section ref={ref} className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-3xl mx-auto text-center">
          <div className={`transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-4">
              Ma vision
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-6">
              Connecter les personnes, les entreprises et les opportunités
            </h2>
            <p className="text-base md:text-lg text-foreground-400 leading-relaxed">
              Je crois profondément que la technologie, lorsqu'elle est conçue avec intention et accessible,
              devient un puissant levier d'inclusion et de développement. Ma vision est de créer des plateformes
              et des outils numériques capables de connecter les personnes, les entreprises, les services
              et les opportunités à l'échelle internationale — en particulier depuis l'Afrique francophone,
              où le potentiel est immense et les outils encore trop fragmentés.
            </p>
          </div>
        </div>
      </section>
    );
  };

  const SkillsReveal = () => {
    const { ref, isVisible } = useScrollReveal(0.1);
    return (
      <section ref={ref} className="py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Expertise
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Mes compétences
            </h2>
            <p className="text-foreground-400 max-w-2xl mx-auto">
              Domaines dans lesquels je travaille au quotidien pour concevoir, construire et faire grandir des solutions numériques.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {skills.map((skill, idx) => (
              <div
                key={skill.name}
                className={`group p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-500 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/10 mb-4 group-hover:bg-primary-500/20 transition-colors">
                  <i className={`${skill.icon} text-2xl text-primary-400`} />
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-50 mb-2">{skill.name}</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">{skill.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const ProjectsReveal = () => {
    const { ref, isVisible } = useScrollReveal(0.1);
    return (
      <section ref={ref} className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
              Réalisations
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Mes réalisations
            </h2>
            <p className="text-foreground-400 max-w-2xl mx-auto">
              Projets conçus et développés avec une vision à long terme.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* WorldApeiron */}
            <div className={`rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-400/40 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="h-52 w-full overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20digital%20ecosystem%20platform%20interface%20with%20connected%20modules%20and%20nodes%2C%20dark%20background%20with%20warm%20gold%20and%20teal%20light%20accents%2C%20futuristic%20technology%20concept%2C%20clean%20minimal%20composition%2C%20editorial%20illustration&width=800&height=450&seq=project-wa-cover&orientation=landscape"
                  alt="WorldApeiron — plateforme digitale conçue par Matthew DOTSE"
                  className="w-full h-full object-cover"
                  title="WorldApeiron — plateforme digitale"
                />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-500/10 text-primary-400 border border-primary-400/20">
                    Ecosystème digital
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-accent-500/10 text-accent-400 border border-accent-400/20">
                    En développement actif
                  </span>
                </div>
                <h3 className="font-heading font-bold text-xl text-foreground-50 mb-2">WorldApeiron</h3>
                <p className="text-sm text-foreground-400 leading-relaxed mb-4">
                  Écosystème numérique complet de 16 modules interconnectés conçu pour l'Afrique francophone.
                  Annuaire d'entreprises, marketplace, espace freelance, immobilier, paiements, IA, formation et plus.
                </p>
                <div className="space-y-2 mb-5">
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0">Rôle :</span>
                    <span className="text-foreground-400">Fondateur, architecte produit et lead développeur</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0">Technologies :</span>
                    <span className="text-foreground-400">React 19, TypeScript, TailwindCSS, Supabase, Vite</span>
                  </div>
                </div>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                >
                  Découvrir le projet
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            {/* Future project placeholder */}
            <div className={`rounded-2xl border border-dashed border-background-300/60 bg-background-50/50 p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[300px] transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '150ms' }}>
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-add-circle-line text-3xl text-foreground-400" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground-400 mb-2">Autres projets à venir</h3>
              <p className="text-sm text-foreground-400 max-w-xs">
                De nouveaux projets et réalisations seront présentés ici prochainement.
                Restez connecté via le blog pour suivre l'évolution.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const CTASection = () => {
    const { ref, isVisible } = useScrollReveal(0.15);
    return (
      <section ref={ref} className="py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto text-center">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-4">
              Travaillons ensemble
            </h2>
            <p className="text-foreground-400 max-w-2xl mx-auto mb-8">
              Vous avez un projet digital, une idée de plateforme ou besoin de conseil stratégique ?
              Discutons de la meilleure façon de le concrétiser.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
              >
                <i className="ri-article-line" />
                Consulter le blog
              </Link>
              <Link
                to="/support"
                className="inline-flex items-center gap-2 px-6 py-3 border border-background-300/60 text-foreground-50 font-semibold rounded-lg hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line" />
                Contact & support
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-32 pb-10 md:pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <HeroReveal />
              <div className="flex justify-center lg:justify-end">
                <div className="relative">
                  <div className="w-64 h-80 md:w-80 md:h-[420px] rounded-2xl overflow-hidden">
                    <img
                      src="https://storage.helloreaddy.io/project_files/72cf10fa-5067-4be9-a1a5-021ba8c29e7b/cbb2e8eb-5ecb-4adf-b5a8-317e07a7924c_compressed_13597.webp"
                      alt="Web Entrepreneur Matthew DOTSE — portrait professionnel"
                      className="w-full h-full object-cover"
                      title="Matthew DOTSE — Web Entrepreneur"
                    />
                  </div>
                  <div className="absolute -bottom-4 -left-4 md:-left-6 px-4 py-3 bg-background-50 border border-background-200/70 rounded-xl shadow-lg">
                    <span className="block text-xs text-foreground-400">Localisation</span>
                    <span className="block text-sm font-semibold text-foreground-50">Lomé, Togo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bio / Qui je suis */}
        <section className="w-full px-4 md:px-8 lg:px-12 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-6">
              Qui je suis
            </h2>
            <div className="space-y-4 text-base text-foreground-400 leading-relaxed">
              <p>
                Je suis <strong className="text-foreground-50">Web Entrepreneur Matthew DOTSE</strong>, basé à Lomé au Togo.
                Mon travail consiste à concevoir, développer et faire évoluer des plateformes numériques
                et des solutions technologiques capables de répondre à des besoins réels dans l'écosystème
                entrepreneurial africain et au-delà.
              </p>
              <p>
                Mon domaine d'activité couvre l'entrepreneuriat web, le développement digital, la création
                de plateformes, les solutions numériques, l'intelligence artificielle, le marketing digital
                et les technologies web. Je me positionne comme un entrepreneur et créateur de solutions
                numériques — celui qui transforme une vision en produit fonctionnel, puis en écosystème vivant.
              </p>
              <p>
                <strong className="text-foreground-50">WorldApeiron</strong> est mon projet phare et la concrétisation de cette vision.
                C'est une plateforme conçue et développée de A à Z, avec pour ambition de devenir le
                point de convergence des acteurs économiques et numériques de l'Afrique francophone.
              </p>
            </div>

            {/* Expertise tags */}
            <div className="flex flex-wrap gap-2 mt-8">
              {['Entrepreneuriat web', 'Plateformes digitales', 'Intelligence artificielle', 'Marketing digital', 'SEO', 'E-commerce', 'Stratégie digitale'].map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-500">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        <VisionReveal />
        <SkillsReveal />
        <ProjectsReveal />
        <CTASection />
      </main>

      <Footer />
    </div>
  );
}

export default AboutPage;